import { apiRoutes } from '@/config/routes'
import type { BenchmarkGroup } from '@/types/analytics'
import type { ChartPeriod } from '@/types/periods'
import type { PerformingVault } from '@/utils/calculateBestPerformingVaults'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { HOUR_IN_SECONDS, SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'

const bestPerformingVaultsQueryKey = ['bestPerformingVaults'] as const

interface BestPerformingVaultsResponse {
  vaults: PerformingVault[]
  period: number
  group?: BenchmarkGroup
  cachedAt?: number
  loadTime: number
  fromCache: boolean
  success: boolean
  error?: string
}

function bestPerformingVaultsQueryOptions(period: ChartPeriod = 365, group: BenchmarkGroup) {
  return queryOptions({
    queryKey: [...bestPerformingVaultsQueryKey, period, group] as const,
    async queryFn() {
      const params = new URLSearchParams({ period: String(period), group: group })
      const response = await fetch(`${apiRoutes.apiBestPerformingVaults}?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch best performing vaults')
      }
      return response.json() as Promise<BestPerformingVaultsResponse>
    },
    retry: 3,
    staleTime: HOUR_IN_SECONDS * SECOND_IN_MILLISECONDS,
  })
}

export const useBestPerformingVaultsQuery = ({
  period = 365,
  group,
  initialData,
}: {
  period: ChartPeriod
  group: BenchmarkGroup
  initialData?: ReturnType<typeof bestPerformingVaultsQueryOptions>['initialData']
}) => {
  const { data, isFetching } = useQuery({
    ...bestPerformingVaultsQueryOptions(period, group),
    initialData,
  })

  return {
    bestPerformingVaults: data?.success ? data.vaults : null,
    dataLastUpdated: data?.success ? data.cachedAt : undefined,
    isFetching,
  }
}
